/**
 * Birthstone Selector
 * Adds chain + selected birthstones to cart separately
 * Each item maintains its own inventory
 * Dynamic pricing from actual product prices
 */

class BirthstoneSelector {
  constructor(container) {
    this.container = container;
    this.maxStones = parseInt(container.dataset.maxStones) || 4;
    this.sectionId = container.dataset.sectionId;
    this.productId = container.dataset.productId;
    this.chainVariantId = container.dataset.chainVariantId;
    
    this.form = container.querySelector('[data-birthstone-form]');
    this.quantitySelect = container.querySelector('[data-birthstone-quantity]');
    this.priceDisplay = container.querySelector('[data-birthstone-total-price]');
    this.submitButton = container.querySelector('[data-birthstone-submit]');
    
    this.variants = JSON.parse(container.querySelector('[data-birthstone-variants]').textContent);
    this.pricing = JSON.parse(container.querySelector('[data-birthstone-pricing]').textContent);
    
    // Create a map of variant id -> variant for quick lookup
    this.variantMap = {};
    this.variants.forEach(v => {
      this.variantMap[v.id] = v;
    });
    
    console.log('BirthstoneSelector initialized');
    console.log('Chain variant ID:', this.chainVariantId);
    console.log('Pricing:', this.pricing);
    
    this.stoneSelects = [];
    this.stoneFields = [];
    this.errorElements = [];
    
    for (let i = 1; i <= this.maxStones; i++) {
      this.stoneSelects.push(container.querySelector(`[data-birthstone-select="${i}"]`));
      this.stoneFields.push(container.querySelector(`[data-birthstone-selection="${i}"]`));
      this.errorElements.push(container.querySelector(`[data-birthstone-error="${i}"]`));
    }
    
    this.init();
  }
  
  init() {
    this.quantitySelect.addEventListener('change', this.onQuantityChange.bind(this));
    
    this.stoneSelects.forEach((select, index) => {
      if (select) {
        select.addEventListener('change', () => this.onStoneChange(index + 1));
      }
    });
    
    if (this.form) {
      this.form.addEventListener('submit', this.onFormSubmit.bind(this));
    }
    
    this.updatePrice();
    this.hideOriginalPrice();
  }
  
  hideOriginalPrice() {
    // Hide the theme's original product price display
    const priceSelectors = [
      '.product__info-wrapper .price',
      '.product-info__price',
      '.price-list',
      '.product__price',
      '.product-single__price',
      '[data-product-price]'
    ];
    
    const productContainer = this.container.closest('.product, .product__info-wrapper, .product-single, [data-product]');
    
    if (productContainer) {
      priceSelectors.forEach(selector => {
        const priceEl = productContainer.querySelector(selector);
        if (priceEl && !priceEl.closest('.birthstone-selector')) {
          priceEl.classList.add('birthstone-price-hidden');
        }
      });
    }
    
    // Also try finding price near the product title
    const productTitle = document.querySelector('.product__title, .product-single__title, h1');
    if (productTitle) {
      const parent = productTitle.parentElement;
      if (parent) {
        const siblingPrice = parent.querySelector('.price, .price-list, .product__price');
        if (siblingPrice && !siblingPrice.closest('.birthstone-selector')) {
          siblingPrice.classList.add('birthstone-price-hidden');
        }
      }
    }
    
    // Move "Vanaf" price to correct position (after product title, before Klarna)
    this.moveStartingPrice();
  }
  
  moveStartingPrice() {
    const startingPrice = this.container.querySelector('.birthstone-selector__starting-price');
    if (!startingPrice) return;
    
    // Find product title
    const productTitle = document.querySelector('.product-meta__title, .product__title, .product-single__title, h1.title, h1');
    if (!productTitle) return;
    
    // Create wrapper for proper positioning
    const wrapper = document.createElement('div');
    wrapper.className = 'birthstone-starting-price-wrapper';
    wrapper.appendChild(startingPrice.cloneNode(true));
    
    // Insert directly after the title
    productTitle.insertAdjacentElement('afterend', wrapper);
    
    // Hide the theme's sale badge since we show our own
    const labelList = document.querySelector('.product-meta__label-list');
    if (labelList) {
      labelList.style.display = 'none';
    }
    
    // Hide original starting price in selector
    startingPrice.remove();
  }
  
  onQuantityChange() {
    const quantity = parseInt(this.quantitySelect.value);
    
    for (let i = 1; i <= this.maxStones; i++) {
      const field = this.stoneFields[i - 1];
      const select = this.stoneSelects[i - 1];
      const error = this.errorElements[i - 1];
      
      if (field && select) {
        if (i <= quantity) {
          field.hidden = false;
          select.disabled = false;
        } else {
          field.hidden = true;
          select.disabled = true;
          select.value = '';
          if (error) error.hidden = true;
        }
      }
    }
    
    this.updatePrice();
    this.checkInventory();
    this.clearAllErrors();
  }
  
  onStoneChange(position) {
    const error = this.errorElements[position - 1];
    if (error) error.hidden = true;
    
    const select = this.stoneSelects[position - 1];
    if (select) select.classList.remove('birthstone-selector__select--error');
    
    // Update product image to show selected stone
    if (select && select.value) {
      const selectedOption = select.options[select.selectedIndex];
      const mediaId = selectedOption.dataset.mediaId;
      
      if (mediaId && mediaId !== 'null') {
        this.updateProductImage(mediaId);
      }
    }
    
    this.checkInventory();
  }
  
  updateProductImage(mediaId) {
    console.log('Updating image to media ID:', mediaId);
    
    // Method 1: Click on the thumbnail button (Prestige/Focal theme)
    const thumbnailButton = document.querySelector(`button[data-media-id="${mediaId}"][data-action="select"]`);
    if (thumbnailButton) {
      thumbnailButton.click();
      return;
    }
    
    // Method 2: Try dot navigation (mobile)
    const dotButton = document.querySelector(`.dots-nav__item[data-media-id="${mediaId}"]`);
    if (dotButton) {
      dotButton.click();
      return;
    }
  }
  
  async onFormSubmit(event) {
    event.preventDefault();
    
    const validation = this.validate();
    
    if (!validation.valid) {
      this.showErrors(validation.errors);
      return false;
    }
    
    // Collect all items to add
    const quantity = parseInt(this.quantitySelect.value);
    const items = [];
    
    // 1. Add the chain
    items.push({
      id: parseInt(this.chainVariantId),
      quantity: 1,
      properties: {
        '_bundled_with': 'Birthstone Hanger',
        '_stone_count': quantity.toString()
      }
    });
    
    // 2. Add each selected birthstone
    for (let i = 1; i <= quantity; i++) {
      const select = this.stoneSelects[i - 1];
      if (select && select.value) {
        const variantId = parseInt(select.value);
        const selectedOption = select.options[select.selectedIndex];
        const variantTitle = selectedOption.dataset.variantTitle;
        
        items.push({
          id: variantId,
          quantity: 1,
          properties: {
            '_bundled_with': 'Birthstone Hanger',
            [`Geboortesteen ${i}`]: variantTitle
          }
        });
      }
    }
    
    console.log('Adding items to cart:', items);
    
    // Show loading state
    if (this.submitButton) {
      this.submitButton.setAttribute('disabled', 'disabled');
      this.submitButton.innerHTML = '<span>TOEVOEGEN...</span>';
    }
    
    try {
      // Add all items to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items: items })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.description || data.message || 'Kon niet toevoegen aan winkelwagen');
      }
      
      console.log('Added to cart successfully:', data);
      
      // Trigger cart update event
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      
      // Reload to show updated cart
      window.location.reload();
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Er is een fout opgetreden: ' + error.message);
      
      // Reset button
      if (this.submitButton) {
        this.submitButton.removeAttribute('disabled');
        this.submitButton.innerHTML = '<span>IN WINKELMAND</span>';
      }
    }
    
    return false;
  }
  
  validate() {
    const quantity = parseInt(this.quantitySelect.value);
    const errors = [];
    
    for (let i = 1; i <= quantity; i++) {
      const select = this.stoneSelects[i - 1];
      if (!select || !select.value) {
        errors.push({
          position: i,
          message: `Selecteer geboortesteen ${i}`
        });
      }
    }
    
    return {
      valid: errors.length === 0,
      errors: errors
    };
  }
  
  showErrors(errors) {
    errors.forEach(error => {
      const errorElement = this.errorElements[error.position - 1];
      const select = this.stoneSelects[error.position - 1];
      
      if (errorElement) {
        errorElement.textContent = error.message;
        errorElement.hidden = false;
      }
      
      if (select) {
        select.classList.add('birthstone-selector__select--error');
        if (error.position === errors[0].position) {
          select.scrollIntoView({ behavior: 'smooth', block: 'center' });
          select.focus();
        }
      }
    });
  }
  
  clearAllErrors() {
    this.errorElements.forEach(error => {
      if (error) error.hidden = true;
    });
    this.stoneSelects.forEach(select => {
      if (select) select.classList.remove('birthstone-selector__select--error');
    });
  }
  
  updatePrice() {
    const quantity = parseInt(this.quantitySelect.value);
    const priceData = this.pricing[quantity.toString()];
    
    if (!priceData) return;
    
    const currentPrice = priceData.price;
    const comparePrice = priceData.compare;
    
    let priceHtml = '';
    
    if (comparePrice && comparePrice > currentPrice) {
      priceHtml = `<s class="birthstone-selector__compare-price">${this.formatMoney(comparePrice)}</s> `;
    }
    
    priceHtml += `<span class="birthstone-selector__current-price">${this.formatMoney(currentPrice)}</span>`;
    
    this.priceDisplay.innerHTML = priceHtml;
  }
  
  checkInventory() {
    const quantity = parseInt(this.quantitySelect.value);
    const selectedCounts = {};
    
    for (let i = 1; i <= quantity; i++) {
      const select = this.stoneSelects[i - 1];
      if (select && select.value) {
        const variantId = select.value;
        selectedCounts[variantId] = (selectedCounts[variantId] || 0) + 1;
      }
    }
    
    for (let i = 1; i <= quantity; i++) {
      const select = this.stoneSelects[i - 1];
      const stockWarning = this.container.querySelector(`[data-birthstone-stock="${i}"]`);
      
      if (!select || !stockWarning) continue;
      
      const variantId = select.value;
      
      if (variantId) {
        const variant = this.variantMap[variantId];
        const inventory = variant ? variant.inventory : 0;
        const totalSelected = selectedCounts[variantId] || 0;
        
        if (inventory > 0 && totalSelected > inventory) {
          stockWarning.textContent = `Let op: slechts ${inventory} op voorraad`;
          stockWarning.hidden = false;
          stockWarning.classList.add('birthstone-selector__stock-warning--error');
        } else if (inventory > 0 && inventory <= 3) {
          stockWarning.textContent = `Nog ${inventory} op voorraad`;
          stockWarning.hidden = false;
          stockWarning.classList.remove('birthstone-selector__stock-warning--error');
        } else {
          stockWarning.hidden = true;
        }
      } else {
        stockWarning.hidden = true;
      }
    }
  }
  
  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2).replace('.', ',');
    return `€${amount}`;
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', function() {
  document.querySelectorAll('[data-birthstone-selector]').forEach(selector => {
    new BirthstoneSelector(selector);
  });
});